import { Form, message } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { mapPackToUpdateRequest } from 'app/hooks/data/core/admin/packs/mapping/form';
import { useCreatePack } from 'app/hooks/data/core/admin/packs/useCreatePack';
import { useDeletePack } from 'app/hooks/data/core/admin/packs/useDeletePack';
import { useUpdatePack } from 'app/hooks/data/core/admin/packs/useUpdatePack';
import { combineDateAndTime, zonedWallClockToUtcIso } from 'app/utils/dates';
import { isHttpErrorBody } from 'app/utils/error';

import type { MenuItemName } from '../menuItem';

import type { PackInitialFormType } from './usePackData';

type UsePackActionsProps = {
  packId?: number;
  isCreatePage: boolean;
  initialValues?: Partial<PackInitialFormType>;
  isFetching?: boolean;
  activeTab?: MenuItemName;
  hotelTimezone?: string;
};

const toSubmittedValues = (
  values: Partial<PackInitialFormType>,
  hotelTimezone?: string
) => {
  const { launchDate, launchTime, ...restValues } = values;

  return {
    ...restValues,
    launchDate:
      launchDate && hotelTimezone
        ? zonedWallClockToUtcIso(
            combineDateAndTime(launchDate, launchTime),
            hotelTimezone
          )
        : null,
  };
};

export const usePackActions = ({
  packId,
  isCreatePage,
  initialValues,
  isFetching,
  activeTab,
  hotelTimezone,
}: UsePackActionsProps) => {
  const [form] = Form.useForm<PackInitialFormType>();
  const history = useHistory();

  const { mode, enterViewMode, enterEditMode } =
    useContext(ViewEditModeContext);

  const { mutateAsync: createPack, isLoading: isCreating } = useCreatePack();
  const { mutateAsync: updatePack, isLoading: isUpdating } = useUpdatePack();
  const { mutateAsync: deletePack } = useDeletePack();

  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (isCreatePage) {
      enterEditMode();
    }
  }, [isCreatePage, enterEditMode]);

  useEffect(() => {
    if (packId && !isFetching && initialValues) {
      form.resetFields();
    }
  }, [packId, isFetching, initialValues, form]);

  const onFinish = useCallback(
    async (values: PackInitialFormType) => {
      const handleError = (error: unknown) => {
        const cause = error instanceof Error ? error.cause : undefined;
        const errorMessage = isHttpErrorBody(cause)
          ? (cause as Record<string, unknown>).message
          : error instanceof Error
          ? error.message
          : 'An unknown error occurred';

        message.error(`Package save failed: ${errorMessage}`, 15);
      };

      const submittedValues = toSubmittedValues(values, hotelTimezone);

      if (isCreatePage) {
        await createPack(submittedValues, {
          onSuccess: (createdPack) => {
            history.push({ pathname: `/packs/${createdPack?.id}` });
            enterViewMode();
          },
          onError: handleError,
        });
      } else if (packId) {
        const payload = mapPackToUpdateRequest(
          submittedValues,
          activeTab,
          initialValues
            ? toSubmittedValues(initialValues, hotelTimezone)
            : undefined
        );

        if (Object.keys(payload).length === 0) {
          enterViewMode();

          return;
        }

        await updatePack(
          { id: packId, payload },
          {
            onSuccess: () => {
              enterViewMode();
            },
            onError: handleError,
          }
        );
      }
    },
    [
      isCreatePage,
      packId,
      createPack,
      history,
      enterViewMode,
      activeTab,
      initialValues,
      updatePack,
      hotelTimezone,
    ]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    enterViewMode();
  }, [form, enterViewMode]);

  const handleDelete = useCallback(async () => {
    if (packId) {
      await deletePack(packId);
      history.push('/packs');
    }
  }, [deletePack, packId, history]);

  const handleSave = useCallback(() => {
    form.submit();
  }, [form]);

  return {
    form,
    mode,
    canSubmit,
    isLoading,
    onFinish,
    onCancel,
    handleDelete,
    handleSave,
    enterEditMode,
    setCanSubmit,
    enterViewMode,
  };
};
